import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {MatPaginatorModule} from "@angular/material/paginator";
import {MatTableModule} from "@angular/material/table";
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from "@angular/material/core";
import {MomentDateAdapter} from "@angular/material-moment-adapter";
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {CommonModule} from '@angular/common';
import {ToastrService} from 'ngx-toastr';
import {AuthService} from './service/auth.service';
import {AuthGuard} from './guards/auth.guard';
import {CanonicalService} from './service/canonical.service';
import {filter, Subscription} from 'rxjs';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatPaginatorModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    CommonModule
  ],
  providers: [
    {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
    {provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS},
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'kubera';
  showBankDetailsPopup: boolean = false;
  showBankDetailsButton: boolean = false;
  currentRoute: string = '';

  bankDetails = [
    { label: 'Bank Name', value: 'ICICI BANK' },
    { label: 'Account Name', value: 'VIINDHYA AU BULLION LLP-GOLD PURCHASE' },
    { label: 'Account Number', value: '386705001436' },
    { label: 'IFSC Code', value: 'ICIC0003867' },
    { label: 'SWIFT Code', value: 'ICICINBBCTS' },
    { label: 'Branch', value: 'LIBERTY X ROADS' }
  ];

  private routerSubscription!: Subscription;

  constructor(
    private router: Router,
    private authService: AuthService,
    private toasterService: ToastrService,
    private canonicalService: CanonicalService
  ) {}

  ngOnInit(): void {
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.currentRoute = event.url;
      this.checkBankDetailsButtonVisibility();
      window.scrollTo(0, 0);
      this.updateCanonicalURL();
    });
    this.currentRoute = this.router.url;
    this.checkBankDetailsButtonVisibility();
    this.updateCanonicalURL();
  }

  private isRouteSecure(routeSnapshot: ActivatedRouteSnapshot): boolean {
    if (routeSnapshot.routeConfig && routeSnapshot.routeConfig.canActivate) {
      if (routeSnapshot.routeConfig.canActivate.includes(AuthGuard)) {
        return true;
      }
    }
    for (const child of routeSnapshot.children) {
      if (this.isRouteSecure(child)) {
        return true;
      }
    }
    return false;
  }

  private updateCanonicalURL(): void {
    const rootSnapshot = this.router.routerState.snapshot.root;
    if (!this.isRouteSecure(rootSnapshot)) {
      this.canonicalService.setCanonicalURL();
    } else {
      this.canonicalService.removeCanonicalURL();
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  callTollFreeNumber(): void {
    window.location.href = 'tel:1800-569-7666';
  }
  private checkBankDetailsButtonVisibility(): void {
    const isLoggedIn = this.isLoggedIn();
    const isAllowedRoute = this.isAllowedRoute();
    this.showBankDetailsButton = isLoggedIn && isAllowedRoute;
  }
  private isAllowedRoute(): boolean {
    const allowedRoutes = [
      '/products',
      '/calculator',
      '/users',
      '/schemes'
    ];
    return allowedRoutes.some(route => this.currentRoute.includes(route));
  }
  openBankDetailsPopup() {
    this.showBankDetailsPopup = true;
    document.body.style.overflow = 'hidden';
  }
  closeBankDetailsPopup() {
    this.showBankDetailsPopup = false;
    document.body.style.overflow = 'auto';
  }

  copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.toasterService.success(`${label} copied to clipboard!`, 'Success');
    }).catch(err => {
      console.error('Failed to copy: ', err);
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        this.toasterService.success(`${label} copied to clipboard!`, 'Success');
      } catch (err) {
        this.toasterService.error('Failed to copy to clipboard', 'Error');
      }
      document.body.removeChild(textArea);
    });
  }

  copyAllBankDetails() {
    const allDetails = this.bankDetails.map(detail => `${detail.label}: ${detail.value}`).join('\n');

    navigator.clipboard.writeText(allDetails).then(() => {
      this.toasterService.success('All bank details copied to clipboard!', 'Success');
    }).catch(err => {
      console.error('Failed to copy: ', err);
      const textArea = document.createElement('textarea');
      textArea.value = allDetails;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        this.toasterService.success('All bank details copied to clipboard!', 'Success');
      } catch (err) {
        this.toasterService.error('Failed to copy to clipboard', 'Error');
      }
      document.body.removeChild(textArea);
    });
  }
  isLoggedIn(): boolean {
    const user = this.authService.getUser();
    return !!user;
  }
}
